import { Icon } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import { type ReactElement, useMemo } from "react";
import Select, {
  type ActionMeta,
  components,
  type DropdownIndicatorProps,
  type OptionProps,
  type SingleValue,
} from "react-select";
import style from "./FilterDropdown.module.css";

export type Option = {
  value: string;
  label: string | ReactElement;
};

export type FilterDropdownProps = {
  options: Option[];
  label: string;
  selectedValue: string;
  handleSelect: (value: string) => void;
  disabled?: boolean;
};

const DropdownIndicator = (props: DropdownIndicatorProps<Option, false>) => {
  return (
    <components.DropdownIndicator {...props}>
      <div
        className={clsx(
          style.arrow,
          props.selectProps.menuIsOpen && style.arrowOpen
        )}
      >
        <Icon icon="arrow-down" size={16} />
      </div>
    </components.DropdownIndicator>
  );
};

const Option = ({ children, ...props }: OptionProps<Option, false>) => {
  return (
    <components.Option {...props}>
      <div className={style.optionContainer}>
        <span>{children}</span>
        {props.isSelected && <Icon icon="check-mark" size={16} />}
      </div>
    </components.Option>
  );
};

const FilterDropdown = ({
  options,
  label,
  selectedValue,
  handleSelect,
  disabled,
}: FilterDropdownProps) => {
  const handleOnChange = (
    option: SingleValue<Option>,
    action: ActionMeta<Option>
  ) => {
    if (action.action === "select-option" && option) {
      handleSelect(option.value);
    }
  };

  const value = useMemo(() => {
    return options.find((option) => option.value === selectedValue);
  }, [selectedValue, options]);

  return (
    <div
      data-testid={`${label.toLowerCase()}-filter-dropdown`}
      className={style.filterDropdown}
    >
      <p
        data-testid={`${label.toLowerCase()}-label`}
        className={style.filterDropdownLabel}
      >
        {label}
      </p>
      <Select
        data-testid={`filter-dropdown-${label.toLowerCase()}`}
        options={options}
        getOptionValue={(option) => option.value}
        isDisabled={disabled || options.length === 1}
        onChange={handleOnChange}
        value={value}
        classNames={{
          control: () => style.dropdownControl,
          option: ({ isSelected }) =>
            clsx(
              style.dropdownOption,
              isSelected && style.dropdownOptionSelected
            ),
          menu: () => style.dropdownMenu,
        }}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator,
          Option,
        }}
      />
    </div>
  );
};

export default FilterDropdown;
